import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Product } from '../../types/product';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { 
  CheckCircleIcon, 
  ShoppingCartIcon,
  ClockIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';
import { usePayments } from '../../hooks/usePayments';
import { useCredentials } from '../../hooks/useCredentials';
import type { PaymentRequest } from '../../types/payment';

const Subscriptions: React.FC = () => {
  const { user } = useAuth();
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  const { createMyPosPayment } = usePayments(user?.organizationId);
  const { credentials } = useCredentials();

  useEffect(() => {
    loadProducts();
  }, [user]);

  const loadProducts = async () => {
    if (!user?.organizationId) {
      console.warn('No organizationId found for user:', user);
      return;
    }

    try {
      setLoading(true);

      console.log('Loading products for organizationId:', user.organizationId);
      console.log('User details:', { 
        uid: user.uid, 
        email: user.email, 
        organizationId: user.organizationId,
        customerId: user.customerId 
      });

      const productsRef = collection(db, 'products');
      
      // First, let's check ALL products to see what organizationIds exist
      const allProductsSnapshot = await getDocs(productsRef);
      console.log('Total products in database:', allProductsSnapshot.size);
      console.log('All products organizationIds:', 
        allProductsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          organizationId: doc.data().organizationId,
          type: doc.data().type,
          webStore: doc.data().webStore,
          isActive: doc.data().isActive
        }))
      );

      const q = query(
        productsRef,
        where('organizationId', '==', user.organizationId)
      );

      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Product[];

      console.log('Products for this organization:', products);
      console.log('Products with details:', products.map(p => ({
        name: p.name,
        type: p.type,
        webStore: p.webStore,
        isActive: p.isActive
      })));

      // Filter in JavaScript to avoid composite index requirement
      const servicesAndPackages = products.filter(
        p => (p.type === 'service' || p.type === 'package') &&
             p.webStore === true &&
             p.isActive === true
      );

      console.log('Filtered services and packages:', servicesAndPackages);
      setAvailableProducts(servicesAndPackages);

    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Hiba a szolgáltatások betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (productId: string) => {
    console.log('Subscribe clicked - User data:', {
      organizationId: user?.organizationId,
      customerId: user?.customerId,
      email: user?.email,
      displayName: user?.displayName
    });

    if (!user?.organizationId) {
      toast.error('Hiányzó szervezet azonosító. Kérjük, jelentkezzen be újra.');
      return;
    }

    if (!user?.customerId) {
      toast.error('Hiányzó ügyfél azonosító. Kérjük, vegye fel a kapcsolatot az ügyfélszolgálattal.');
      return;
    }

    try {
      setProcessingPayment(true);
      
      // Find the product
      const product = availableProducts.find(p => p.id === productId);
      if (!product) {
        throw new Error('Termék nem található');
      }

      // Find MyPOS credential
      const myposCredential = credentials.find(c => c.type === 'mypos' && c.isActive);
      if (!myposCredential) {
        toast.error('MyPOS fizetési beállítás nincs konfigurálva. Kérjük, vegye fel a kapcsolatot az ügyfélszolgálattal.');
        return;
      }

      // Generate unique order ID
      const orderID = `SUB-${Date.now()}-${productId.substring(0, 8)}`;
      
      // Get product price (use netPrice or grossPrice)
      const amount = product.grossPrice || product.netPrice || 0;
      if (amount <= 0) {
        toast.error('A termék ára nincs beállítva');
        return;
      }

      // Prepare payment request (avoid undefined values for Firestore)
      const paymentRequest: PaymentRequest = {
        orderId: orderID,
        purpose: 'subscription',
        amount,
        currency: product.currency || 'HUF',
        customerName: user.displayName || user.email || 'Ügyfél',
        organizationId: user.organizationId,
        customerId: user.customerId,
        description: `Előfizetés: ${product.name}`,
        note: `${product.type === 'service' ? 'Szolgáltatás' : 'Csomag'} előfizetés`,
        okUrl: `${window.location.origin}/customer-portal?payment=success&orderId=${orderID}`,
        cancelUrl: `${window.location.origin}/customer-portal?payment=cancelled`,
        notifyUrl: `${window.location.origin}/api/mypos/webhook`,
        metadata: {
          productId: product.id,
          productName: product.name,
          productType: product.type,
          cartItems: [{
            name: product.name,
            quantity: 1,
            price: amount,
            amount: amount
          }]
        }
      };

      // Add optional fields only if they exist
      if (user.email) paymentRequest.customerEmail = user.email;
      if (user.phoneNumber) paymentRequest.customerPhone = user.phoneNumber;

      toast.loading('Fizetési folyamat indítása...', { id: 'payment-init' });

      // Create MyPOS payment
      const response = await createMyPosPayment(myposCredential.id, paymentRequest);

      toast.dismiss('payment-init');

      if (response.success && response.paymentUrl) {
        toast.success('Átirányítás a fizetési oldalra...');
        // Redirect to MyPOS payment page
        window.location.href = response.paymentUrl;
      } else {
        throw new Error('Nem sikerült létrehozni a fizetési linket');
      }

    } catch (error: any) {
      console.error('Error subscribing:', error);
      toast.error(error.message || 'Hiba az előfizetés során');
      setProcessingPayment(false);
    }
  };

  const getProductTypeLabel = (type: Product['type']) => {
    const labels = {
      service: { label: 'Szolgáltatás', icon: '🔧', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' },
      package: { label: 'Csomag', icon: '📦', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300' },
      product: { label: 'Termék', icon: '🛍️', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' }
    };
    return labels[type];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Elérhető szolgáltatások és csomagok
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Válassz az alábbi szolgáltatások és csomagok közül
        </p>
      </div>

      {availableProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {availableProducts.map((product) => {
            const typeInfo = getProductTypeLabel(product.type);
            
            return (
              <div
                key={product.id}
                className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 transition-all p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${typeInfo.color}`}>
                        {typeInfo.icon} {typeInfo.label}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                      {product.name}
                    </h3>
                    {product.code && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Kód: {product.code}
                      </p>
                    )}
                  </div>
                </div>

                {product.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                    {product.description}
                  </p>
                )}

                {product.netPrice && (
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-gray-900 dark:text-white">
                        {product.netPrice.toLocaleString('hu-HU')}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {product.currency || 'HUF'}
                      </span>
                      {product.type === 'service' && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          /{product.unit || 'óra'}
                        </span>
                      )}
                    </div>
                    {product.taxRate && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        + {product.taxRate} ÁFA
                      </p>
                    )}
                  </div>
                )}

                {product.type === 'package' && product.packageDetails && (
                  <div className="mb-6 space-y-2">
                    {product.packageDetails.services && product.packageDetails.services.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Csomag tartalma:
                        </p>
                        <div className="space-y-1">
                          {product.packageDetails.services.slice(0, 3).map((service, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {service.name}
                              </span>
                            </div>
                          ))}
                          {product.packageDetails.services.length > 3 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 pl-6">
                              +{product.packageDetails.services.length - 3} további szolgáltatás
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {product.packageDetails.totalHours && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <ClockIcon className="h-4 w-4" />
                        <span>{product.packageDetails.totalHours} óra</span>
                      </div>
                    )}
                  </div>
                )}

                <Button
                  onClick={() => handleSubscribe(product.id)}
                  className="w-full"
                  variant="primary"
                >
                  <ShoppingCartIcon className="h-5 w-5" />
                  Előfizetek
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nincs elérhető szolgáltatás
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Jelenleg nincs elérhető szolgáltatás vagy csomag
          </p>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
